/**
 * useBulkEmailStats — E-93 Story 93.3.
 *
 * TanStack Query wrapper around `fetchBulkEmailStats`. The stats are
 * derived data (aggregated at request time from BulkEmailBatch), so we
 * cache them for 60s and refetch on window focus — stats can drift while
 * the tab is backgrounded and coming back should show fresh numbers.
 */

import { fetchBulkEmailStats, type BulkEmailStats } from '@/api/bulk-email';
import { retryUpTo } from '@/lib/query-retry';
import { useQuery } from '@tanstack/react-query';

export interface UseBulkEmailStatsResult {
  data: BulkEmailStats | undefined;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<unknown>;
}

export function useBulkEmailStats(windowDays?: number): UseBulkEmailStatsResult {
  const query = useQuery({
    queryKey: ['bulk-email-stats', windowDays ?? 'default'],
    queryFn: () => fetchBulkEmailStats(windowDays),
    // Stats aren't super hot — 60s cache is generous enough that the
    // cards don't flicker on every BulkEmailPage re-mount but fresh
    // enough that a user returning after coffee sees up-to-date numbers.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: retryUpTo(1),
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,
  };
}
